import React, { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getStorage, getDownloadURL, ref as storageRef } from 'firebase/storage';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';

const labels = {
  before: 'Before work', after: 'After work', equipment: 'Equipment',
  serial: 'Serial number', asset_tag: 'Asset tag',
};

/** Shown ONLY to admins on previously approved reports. Never auto-publishes. */
export default function ServiceEvidenceAddendum({ job }) {
  const { userProfile } = useAuth();
  const isAdmin = userProfile?.role === 'admin';
  const [evidence, setEvidence] = useState([]);
  const [report, setReport] = useState(null);
  const [urls, setUrls] = useState({});
  const [loadError, setLoadError] = useState('');
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const jobId = job?.id;
  const evidencePaths = evidence.map(p => p.path).join('|');

  useEffect(() => {
    if (!isAdmin || !jobId) return undefined;
    const unsubscribeEvidence = onSnapshot(doc(db,'serviceEvidence',jobId), snap => {
      setEvidence(snap.exists() && Array.isArray(snap.data()?.photos) ? snap.data().photos : []);
      setAgree(false);
    }, err => setError(`Cannot read registered photos: ${err.message}`));
    const unsubscribeReport = onSnapshot(doc(db,'serviceReports',jobId), snap => {
      setReport(snap.exists() ? snap.data() : null);
    }, err => setError(`Cannot read approved report: ${err.message}`));
    return () => { unsubscribeEvidence(); unsubscribeReport(); };
  }, [isAdmin, jobId]);

  useEffect(() => {
    if (!isAdmin || !evidence.length) { setUrls({}); return undefined; }
    let active = true;
    setLoadError('');
    setUrls({});
    Promise.all(evidence.map(async photo => {
      const url = await getDownloadURL(storageRef(getStorage(),photo.path));
      return [photo.path,url];
    })).then(entries => { if(active) setUrls(Object.fromEntries(entries)); })
      .catch(err => { if(active) setLoadError(`Could not preview every photograph: ${err.message}`); });
    return () => {active=false;};
  }, [isAdmin,evidencePaths]);

  const reportPaths = useMemo(() => new Set(
    Array.isArray(report?.photos) ? report.photos.map(p => p.path) : []
  ), [report]);
  const publishedPaths = new Set(Array.isArray(job?.serviceReport?.photos) ? job.serviceReport.photos.map(p=>p.path) : []);
  const notYetApproved = evidence.filter(p => !reportPaths.has(p.path) || !publishedPaths.has(p.path));
  const previewsReady = evidence.length > 0 && evidence.every(p=>Boolean(urls[p.path])) && !loadError;
  const eligible = isAdmin && job.status === 'completed' &&
    job.serviceReportStatus === 'approved' && report?.status === 'approved';

  if (!isAdmin || job?.status !== 'completed') return null;
  const publish = async () => {
    setBusy(true); setError(''); setNotice('');
    try {
      const result = await httpsCallable(getFunctions(), 'approveServiceEvidenceAddendum')({
        jobId,
        acknowledgment: true,
        approvedPaths: evidence.map(p=>p.path),
      });
      setAgree(false);
      setNotice(result.data?.changed
        ? `${result.data.count} photographs approved and attached to the client report. Refresh the client PDF.`
        : 'All registered photographs are already included in the approved report.');
    } catch(err) {
      setError(err?.message || 'Unable to approve the photo addendum.');
    } finally { setBusy(false); }
  };

  return (
    <section className="rounded-xl border border-gray-300 bg-white p-5 space-y-4 text-[#111111]" aria-label="Approve photographic evidence addendum">
      <div>
        <h3 className="text-lg font-bold text-[#111111]">Photographic evidence addendum</h3>
        <p className="mt-1 text-sm text-[#111111]">Review the existing registered work photographs before adding them to this already approved report. This will not reopen the job or affect payment.</p>
      </div>
      {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-red-900 text-sm">{error}</p>}
      {notice && <p role="status" className="rounded-lg bg-green-50 p-3 text-green-900 text-sm">{notice}</p>}
      {loadError && <p role="alert" className="text-red-900 text-sm">{loadError}</p>}
      {!eligible && <p className="text-sm">The original service report must be approved before adding an evidence addendum.</p>}
      {eligible && !evidence.length && <p className="text-sm">No registered photographs exist for this work order.</p>}
      {eligible && evidence.length > 0 && <>
        <p className="text-sm font-semibold text-[#111111]">Registered: {evidence.length} · Not yet in approved report: {notYetApproved.length}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {evidence.map(p => <figure key={p.path} className="border border-gray-300 rounded-lg p-3 bg-white">
            {urls[p.path] ? (
              <a target="_blank" rel="noopener noreferrer" href={urls[p.path]} title="Inspect full-size photo">
                <img className="w-full h-44 object-contain bg-gray-100 rounded" src={urls[p.path]}
                  alt={`${labels[p.category] || 'Work evidence'} photo`}
                  onError={() => setLoadError('One or more photographs could not be loaded. Do not approve.')}/>
              </a>
            ) : <div className="h-44 bg-gray-100 grid place-items-center text-sm">Loading preview…</div>}
            <figcaption className="mt-2 text-sm text-[#111111]">
              <strong>{labels[p.category] || p.category}</strong>
              {p.caption && <span className="block mt-1">{p.caption}</span>}
              <span className="block mt-1">{reportPaths.has(p.path) ? 'Already in report' : 'Awaiting addendum'}</span>
            </figcaption>
          </figure>)}
        </div>
        {notYetApproved.length > 0 ? <>
          <label className="flex gap-3 text-sm text-[#111111] items-start">
            <input type="checkbox" checked={agree} onChange={e=>setAgree(e.target.checked)}
              className="mt-1" disabled={!previewsReady || busy}/>
            <span>I have reviewed every photograph above and authorize adding these images to the client-visible service report.</span>
          </label>
          <button type="button" disabled={!agree || busy || !previewsReady} onClick={publish}
            className="px-5 py-3 rounded-lg bg-[#2f79d4] !text-white font-semibold disabled:opacity-50">
            {busy ? 'Approving…' : 'Approve photographs for client report'}
          </button>
        </> : <p className="text-sm font-semibold text-[#111111]">All registered photographs are already included in the approved report.</p>}
      </>}
    </section>
  );
}
